import type { ReactNode } from 'react';

import { makeStyles } from '@material-ui/core';
import { ToggleButtonGroup } from '@material-ui/lab';

import { RiBarChartHorizontalLine, RiListCheck } from '@remixicon/react';

import { TooltipToggleButton } from '../TooltipToggleButton';

export type ViewMode = 'Table' | 'Timeline' | string;

interface ViewModeOption {
  icon: ReactNode;
  name: ViewMode;
}

export const DEFAULT_VIEW_MODE_OPTIONS: ViewModeOption[] = [
  { icon: <RiListCheck size={20} />, name: 'Table' },
  { icon: <RiBarChartHorizontalLine size={20} />, name: 'Timeline' },
];

const useStyles = makeStyles({
  toggleButton: {
    height: 'var(--encore-spacing-looser-2)',
    width: 'var(--encore-spacing-looser-2)',
    borderColor: 'var(--decorative-subdued)',
  },
});

type ViewModeToggleProps = {
  mode: ViewMode;
  onModeChange: (newViewMode: ViewMode) => void;
  options?: ViewModeOption[];
  disabled?: boolean;
};

export const ViewModeToggle = ({
  mode,
  onModeChange,
  options = DEFAULT_VIEW_MODE_OPTIONS,
  disabled,
}: ViewModeToggleProps) => {
  const classes = useStyles();

  const changeMode = (newMode: ViewMode | null) =>
    onModeChange(newMode === null ? mode : newMode);

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={mode}
      onChange={(_e, value) => changeMode(value)}
      data-testid="toggle-view-mode"
     data-track-source-anchor="c9f0863ad9;view-mode-toggle-button-group">
      {options.map(viewMode => (
        <TooltipToggleButton
          key={viewMode.name}
          className={classes.toggleButton}
          value={viewMode.name}
          disabled={disabled}
          aria-label={viewMode.name}
          TooltipProps={{ title: viewMode.name, enterDelay: 1000 }}
         data-track-source-anchor="d81c79b456;view-mode-toggle-tooltip-toggle-button">
          {viewMode.icon}
        </TooltipToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
